import React from 'react';
import { shallowEqual } from 'react-redux';
import { contrastTextColor, isBrightColor } from '@sendent/nextcloud-api';
import type { ResolvedThemeColors } from '@sendent/nextcloud-api';
import {
    Dialog,
    FontIcon,
    IButtonStyles,
    IconButton,
    Image,
    ITheme,
    mergeStyleSets,
    PrimaryButton,
    useTheme,
    ZIndexes,
} from '@fluentui/react';
import {
    appStyles,
    releaseText,
    resolveThemeColors,
    Utilities,
} from '../../helpers';
import { DISPLAY_FONT_STACK, tint } from '../../helpers/theme';
import { useAppSelector } from '../../store';
import type { ReleaseEntry } from '../../helpers/release-notes';
import { selectTranslate } from '../../store/selectors/ui';

const TITLE_ID = 'whatsnew-title';
const HEADLINE_ID = 'whatsnew-headline';
const CARD_LAYER_Z_INDEX = ZIndexes.Layer + 1;
const NEXTCLOUD_LOGO = 'assets/nextcloud_logo.svg';
const LOGO_TILE_SIZE = 52;
const LOGO_IMAGE_SIZE = 34;
const EXPLORER_PAGE = 'explorer';
const EXPLORER_DIALOG_WIDTH = '480px';
const COLUMN = { display: 'flex', flexDirection: 'column' } as const;
const FILL = { ...COLUMN, flexGrow: 1, minHeight: 0 };

const getStyles = (
    colors: ResolvedThemeColors,
    theme: ITheme,
    wide: boolean,
) => {
    const divider = `1px solid ${theme.semanticColors.bodyDivider}`;
    const shade = theme.palette.neutralLighter;
    const closeHover = {
        color: contrastTextColor(colors.primaryHover),
        backgroundColor: colors.primaryHover,
    };
    const classes = mergeStyleSets({
        band: {
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            padding: '22px 12px 22px 20px',
            backgroundColor: colors.primary,
            color: colors.primaryFont,
            borderBottom: `1px solid ${colors.primaryHover}`,
        },
        bandLogo: {
            flex: '0 0 auto',
            filter: isBrightColor(colors.primary) ? 'invert(1)' : 'none',
        },
        bandTitle: {
            flex: '1 1 auto',
            minWidth: 0,
            margin: '0 0 0 14px',
            fontFamily: DISPLAY_FONT_STACK,
            fontSize: 24,
            lineHeight: '1.2',
            fontWeight: 700,
            color: colors.primaryFont,
        },
        body: {
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            backgroundColor: colors.popupBg,
            color: colors.appBgText,
        },
        release: {
            selectors: { '&:not(:first-child)': { borderTop: divider } },
        },
        hero: { padding: '24px 20px 20px' },
        logos: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 16,
        },
        logoTile: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: LOGO_TILE_SIZE,
            height: LOGO_TILE_SIZE,
            borderRadius: 4,
            border: divider,
            backgroundColor: colors.popupBg,
        },
        logoArrow: {
            margin: '0 14px',
            fontSize: 16,
            color: colors.appBgText,
            opacity: 0.55,
        },
        eyebrow: {
            margin: 0,
            fontSize: 12,
            lineHeight: '1.4',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: colors.icon,
        },
        headline: {
            margin: '6px 0 0',
            fontFamily: DISPLAY_FONT_STACK,
            fontSize: 20,
            lineHeight: '1.3',
            fontWeight: 600,
            color: colors.appBgText,
        },
        lead: {
            margin: '10px 0 0',
            fontSize: 14,
            lineHeight: '1.5',
            color: colors.appBgText,
            opacity: 0.85,
        },
        version: {
            margin: '6px 0 0',
            fontSize: 12,
            lineHeight: '1.4',
            color: colors.appBgText,
            opacity: 0.6,
        },
        notes: {
            margin: 0,
            padding: 20,
            listStyle: 'none',
            borderTop: divider,
            backgroundColor: shade,
        },
        note: {
            display: 'flex',
            alignItems: 'flex-start',
            selectors: { '&:not(:first-child)': { marginTop: 18 } },
        },
        noteIcon: {
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 4,
            backgroundColor: tint(colors.primary, 0.16),
            color: colors.icon,
        },
        noteGlyph: { fontSize: 15 },
        noteText: { minWidth: 0, marginLeft: 14 },
        noteTitle: {
            margin: 0,
            fontSize: 15,
            lineHeight: '1.35',
            fontWeight: 600,
            color: colors.appBgText,
        },
        noteBody: {
            margin: '3px 0 0',
            fontSize: 13,
            lineHeight: '1.45',
            color: colors.appBgText,
            opacity: 0.8,
        },
        footer: {
            flex: '0 0 auto',
            padding: '16px 20px 20px',
            backgroundColor: shade,
            borderTop: divider,
        },
    });

    const closeStyles: IButtonStyles = {
        root: {
            flex: '0 0 auto',
            width: 32,
            height: 32,
            marginLeft: 8,
            color: colors.primaryFont,
            backgroundColor: 'transparent',
        },
        rootHovered: closeHover,
        rootPressed: closeHover,
        rootFocused: { color: colors.primaryFont },
        icon: { fontSize: 14, color: colors.primaryFont },
        iconHovered: { color: closeHover.color },
        iconPressed: { color: closeHover.color },
    };

    const dialogStyles = {
        main: [
            appStyles.DialogStyle.main,
            {
                ...COLUMN,
                overflow: 'hidden' as const,
                overscrollBehavior: 'contain' as const,
            },
            wide && {
                width: `${EXPLORER_DIALOG_WIDTH} !important`,
                maxWidth: 'calc(100% - 32px) !important',
            },
        ],
    };

    return { classes, closeStyles, dialogStyles };
};

type WhatsNewDialogProps = {
    releases: ReleaseEntry[];
    hidden: boolean;
    onDismiss: () => void;
};

const WhatsNewDialog = ({
    releases,
    hidden,
    onDismiss,
}: WhatsNewDialogProps) => {
    const colors = useAppSelector(resolveThemeColors, shallowEqual);
    const theme = useTheme();
    const translate = useAppSelector(selectTranslate);
    const lang = useAppSelector((state) => state.uiReducer.lang);
    const pageMode = useAppSelector((state) => state.uiReducer.pageMode);

    const { classes, closeStyles, dialogStyles } = getStyles(
        colors,
        theme,
        pageMode === EXPLORER_PAGE,
    );

    return (
        <Dialog
            hidden={hidden}
            onDismiss={onDismiss}
            styles={dialogStyles}
            modalProps={{
                titleAriaId: TITLE_ID,
                subtitleAriaId: HEADLINE_ID,
                layerProps: {
                    styles: {
                        root: { zIndex: CARD_LAYER_Z_INDEX },
                    },
                },
                styles: {
                    scrollableContent: { ...FILL, overflow: 'hidden' },
                },
            }}
            dialogContentProps={{
                styles: {
                    header: { padding: 0, height: 0 },
                    title: { display: 'none' },
                    content: FILL,
                    inner: { ...FILL, padding: '0 !important' },
                    innerContent: FILL,
                },
            }}
        >
            <div className={classes.band}>
                <Image
                    className={classes.bandLogo}
                    src={NEXTCLOUD_LOGO}
                    alt=""
                    width={54}
                    height={27}
                />
                <h1 id={TITLE_ID} className={classes.bandTitle}>
                    {translate('whatsnew_title')}
                </h1>
                <IconButton
                    styles={closeStyles}
                    iconProps={{ iconName: 'Cancel' }}
                    ariaLabel={translate('whatsnew_close')}
                    onClick={onDismiss}
                />
            </div>
            <div className={classes.body}>
                {releases.map((entry, index) => (
                    <section key={entry.version} className={classes.release}>
                        <div className={classes.hero}>
                            {entry.logoChange && (
                                <div
                                    className={classes.logos}
                                    aria-hidden="true"
                                >
                                    <span className={classes.logoTile}>
                                        <Image
                                            src={entry.logoChange.from}
                                            alt=""
                                            width={LOGO_IMAGE_SIZE}
                                            height={LOGO_IMAGE_SIZE}
                                        />
                                    </span>
                                    <FontIcon
                                        iconName="Forward"
                                        className={classes.logoArrow}
                                    />
                                    <span className={classes.logoTile}>
                                        <Image
                                            src={entry.logoChange.to}
                                            alt=""
                                            width={LOGO_IMAGE_SIZE}
                                            height={LOGO_IMAGE_SIZE}
                                        />
                                    </span>
                                </div>
                            )}
                            {entry.eyebrow && (
                                <p className={classes.eyebrow}>
                                    {releaseText(entry.eyebrow, lang)}
                                </p>
                            )}
                            <h2
                                id={index === 0 ? HEADLINE_ID : undefined}
                                className={classes.headline}
                            >
                                {releaseText(entry.title, lang)}
                            </h2>
                            <p className={classes.lead}>
                                {releaseText(entry.subtitle, lang)}
                            </p>
                            <p className={classes.version}>
                                {Utilities.formatString(
                                    translate('whatsnew_version'),
                                    entry.version,
                                )}
                            </p>
                        </div>
                        <ul role="list" className={classes.notes}>
                            {entry.notes.map((note, noteIndex) => (
                                <li key={noteIndex} className={classes.note}>
                                    <span
                                        className={classes.noteIcon}
                                        aria-hidden="true"
                                    >
                                        <FontIcon
                                            iconName={note.icon}
                                            className={classes.noteGlyph}
                                        />
                                    </span>
                                    <div className={classes.noteText}>
                                        <h3 className={classes.noteTitle}>
                                            {releaseText(note.title, lang)}
                                        </h3>
                                        <p className={classes.noteBody}>
                                            {releaseText(note.body, lang)}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
            <div className={classes.footer}>
                <PrimaryButton
                    styles={appStyles.primaryButton}
                    style={{ width: '100%' }}
                    text={translate('whatsnew_dismiss')}
                    onClick={onDismiss}
                />
            </div>
        </Dialog>
    );
};

export default WhatsNewDialog;
